// useMyProducts — 상품 관리 본문의 상태와 API. MyProductManagement.jsx 에서 그대로 옮겼다(동작 변화 없음).
//   백엔드 /admin/ticket-types 는 requesterScope 로 요청자 회사 상품만 조회/생성/수정한다.
import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';

export function useMyProducts() {
  const { showToast } = useToast();
  const base = '/api/admin/ticket-types';

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [confirmId, setConfirmId] = useState(null);
  const [validId, setValidId] = useState(null);
  const [validVal, setValidVal] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [pRes, cRes] = await Promise.all([
      apiClient.get(base),
      apiClient.get('/api/admin/product-categories'),
    ]);
    setLoading(false);
    if (pRes.ok) setProducts(pRes.data?.data || []);
    if (cRes.ok) {
      const cats = cRes.data?.data || [];
      setCategories(cats);
      if (cats.length > 0) setNewCategory((prev) => prev || cats[0]?.name || '');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // 고객 테넌트(전역 카테고리 미노출)는 카테고리를 자유 텍스트로 직접 관리
  const freeCategory = categories.length === 0;
  // 자기 상품의 카테고리 값들 (자동완성·그룹 표시용)
  const myCatNames = [...new Set(products.map((p) => String(p.category || '').trim()).filter(Boolean))].sort();

  async function add() {
    const name = newName.trim();
    if (!name) { showToast('상품명을 입력하세요.', 'error'); return; }
    const category = newCategory.trim();
    // 운영자(전역 카테고리 존재)는 카테고리 선택 필수 유지, 테넌트는 자유 입력(비우면 미분류)
    if (!freeCategory && !category) { showToast('카테고리를 선택하세요.', 'error'); return; }
    setSaving(true);
    // 목록에 없는 이름이면 카테고리부터 만든다 (2026-09-22: 추가·선택을 한 칸에서).
    //   서버가 동적 관리 권한을 요구하므로, 권한이 없으면 여기서 실패하고 상품은 만들지 않는다.
    const isNewCategory = Boolean(category) && !categories.some((c) => c.name === category);
    if (!freeCategory && isNewCategory) {
      const cr = await apiClient.post('/api/admin/product-categories', { name: category });
      if (!cr.ok) {
        setSaving(false);
        showToast(cr.data?.message || '카테고리를 만들지 못했습니다.', 'error');
        return;
      }
    }
    const res = await apiClient.post(base, { name, category: category || null });
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '상품 추가 실패', 'error'); return; }
    showToast(isNewCategory ? `[${category}] 카테고리를 만들고 상품을 추가했습니다.` : '상품을 추가했습니다.', 'success');
    setNewName('');
    if (freeCategory) setNewCategory('');
    await load();
    return isNewCategory ? category : null;
  }

  async function saveEdit(id) {
    const name = editName.trim();
    if (!name) { showToast('상품명을 입력하세요.', 'error'); return; }
    setSaving(true);
    const res = await apiClient.put(`${base}/${id}`, { name, category: editCategory.trim() || null });
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '수정 실패', 'error'); return; }
    setEditId(null);
    await load();
  }

  async function remove(id) {
    setSaving(true);
    const res = await apiClient.delete(`${base}/${id}`);
    setSaving(false);
    setConfirmId(null);
    if (!res.ok) { showToast(res.data?.message || '삭제 실패', 'error'); return; }
    showToast('상품을 비활성화했습니다.', 'success');
    await load();
  }

  async function saveValidity(id) {
    const months = validVal === '' ? null : Number(validVal);
    if (months !== null && (!Number.isInteger(months) || months < 1 || months > 120)) {
      showToast('유효기간은 1~120 사이 정수여야 합니다.', 'error'); return;
    }
    setSaving(true);
    const res = await apiClient.put(`${base}/${id}/validity`, { months });
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '유효기간 저장 실패', 'error'); return; }
    setValidId(null);
    await load();
  }

  // 그룹: 운영자 = 전역 카테고리 순서, 테넌트 = 자기 상품의 카테고리 값들
  const grouped = freeCategory
    ? myCatNames.map((name) => ({ key: `my-${name}`, name, tag: null, items: products.filter((p) => String(p.category || '').trim() === name) }))
    : categories.map((c) => ({
      key: c.id,
      name: c.name,
      // 내부/외부 배지는 없앴다(2026-09-20) — 회사마다 쓸 분류는 "회사에 붙이기" 에서 정한다.
      tag: null,
      // 기본상품 = 미팅지도 계약완료 창에 자동으로 들어가는 상품. 머리줄에서 바로 고르게 한다(2026-09-20).
      defaultProductId: c.default_product_id ?? null,
      items: products.filter((p) => p.category === c.name),
    }));
  const orphan = freeCategory
    ? products.filter((p) => !String(p.category || '').trim())
    : products.filter((p) => !categories.some((c) => c.name === p.category));

  return {
    products, categories, loading, saving, load,
    newName, setNewName, newCategory, setNewCategory,
    editId, setEditId, editName, setEditName, editCategory, setEditCategory,
    confirmId, setConfirmId, validId, setValidId, validVal, setValidVal,
    freeCategory, myCatNames, grouped, orphan,
    add, saveEdit, remove, saveValidity,
  };
}
